import { Link, useLocation } from 'react-router-dom';
import { Home, Compass, Search, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect, useCallback } from 'react';
import { useProfilePrefetch } from '@/hooks/useProfilePrefetch';

export function MobileNavigation() {
  const location = useLocation();
  const { user } = useAuth();
  const { prefetchProfile } = useProfilePrefetch();
  const [username, setUsername] = useState<string>('');

  const handleProfilePrefetch = useCallback(() => {
    if (username) {
      prefetchProfile(username);
    }
  }, [username, prefetchProfile]);

  useEffect(() => {
    const fetchUsername = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', user.id)
        .single();
      if (data?.username) {
        setUsername(data.username);
      }
    };
    fetchUsername();
  }, [user]);

  if (!user) return null;

  const navItems = [
    { href: '/', label: 'Home', icon: Home },
    { href: '/search', label: 'Search', icon: Search },
    { href: '/discover', label: 'Discover', icon: Compass },
    { href: username ? `/u/${username}` : '/profile', label: 'Profile', icon: User },
  ];

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[9999] bg-background border-t border-border/50 shadow-[0_-2px_10px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-around py-1.5 px-4">
        {navItems.map(({ href, label, icon: Icon }) => {
          const isActive = location.pathname === href;
          const isProfileLink = label === 'Profile';

          return (
            <Link
              key={href}
              to={href}
              onMouseEnter={isProfileLink ? handleProfilePrefetch : undefined}
              onTouchStart={isProfileLink ? handleProfilePrefetch : undefined}
              className={cn(
                "relative flex flex-col items-center justify-center py-2 px-4 rounded-xl transition-all duration-200",
                "min-h-[52px] min-w-[48px] touch-target tap-feedback",
                isActive
                  ? "text-primary bg-primary/15 scale-[1.02]"
                  : "text-muted-foreground active:text-foreground active:bg-muted/50"
              )}
            >
              <Icon className={cn(
                "w-5 h-5 mb-0.5 transition-transform",
                isActive && "scale-110"
              )} />
              <span className={cn(
                "text-[10px] font-medium transition-colors",
                isActive && "font-semibold"
              )}>{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
